import classNames from 'classnames';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import alertCircleIcon from 'assets/images/alert-circle.png';
import { Button } from 'components/Button/Button';
import { TextLengthCounter } from 'components/TextLengthCounter/TextLengthCounter';
import { PolymorphicElement } from 'types/PolymorphicElement';
import { Text } from '../Typography';
import style from './TextArea.scss';

export type TextVariant = 'xl' | 'lg' | 'md' | 'sm' | 'xs';
('xs');
export type TextWeight = 400 | 500 | 600 | 700;

export type TextAreaProps = PolymorphicElement<'textarea'> & {
  className?: string;
  label?: React.ReactNode;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  error?: React.ReactNode;
  helperText?: React.ReactNode;
  isMandatory?: boolean;
  rows?: number;
  cols?: number;
  onClick?: React.MouseEvent<HTMLDivElement>;
  onSendButton?: () => void;
  sendButtonIcon?: React.ReactNode;
  sendButtonDisabled?: boolean;
  enclosedContent?: React.ReactNode;
  maxLength?: number;
  isTransparent?: boolean;
  autoResize?: boolean;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>((props, ref) => {
  const {
    className,
    label,
    leading,
    trailing,
    onSendButton,
    maxLength,
    isTransparent,
    autoResize,
    sendButtonIcon,
    sendButtonDisabled,
    enclosedContent,
    error,
    helperText,
    onClick,
    rows = 4,
    cols = 50,
    ...rest
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const internalRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => internalRef.current!, []);

  useEffect(() => {
    if (autoResize && internalRef.current) {
      const el = internalRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [props.value, autoResize]);

  const handleOnBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    props?.onBlur?.(event);
  };

  const handleOnFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    props?.onFocus?.(event);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    props?.onChange?.(e);
    if (autoResize && internalRef.current) {
      const el = internalRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  return (
    <>
      <div className={classNames(style.container, className)} onClick={onClick as any}>
        {label && (
          <Text variant="sm" className={style.label}>
            {label}
            {props?.isMandatory && <span> *</span>}
          </Text>
        )}
        <div
          className={classNames(
            style.inputContainer,
            props.disabled && style.disabled,
            error && style.containerError,
            isFocused && style.isFocused,
            isTransparent && style.isTransparent
          )}
        >
          <div className={style.inputSubContainer}>
            {leading && <span className={style.leading}>{leading}</span>}

            <textarea
              style={{ resize: 'none' }}
              ref={internalRef}
              maxLength={maxLength}
              {...rest}
              onChange={handleOnChange}
              rows={rows}
              cols={cols}
              onBlur={handleOnBlur}
              onFocus={handleOnFocus}
              className={style.input}
            />

            {trailing && !error && <span className={style.trailing}>{trailing}</span>}
          </div>
          {enclosedContent}
          {(onSendButton || maxLength) && (
            <div className={style.sendButtonContainer}>
              {onSendButton && (
                <Button className={style.sendButton} variant="contained" onClick={onSendButton} disabled={sendButtonDisabled}>
                  {sendButtonIcon}
                </Button>
              )}
            </div>
          )}
          {/* {error && <AlertIcon></AlertIcon>} */}
        </div>
        {!error && <TextLengthCounter length={props.value ? props.value.toString().length : 0} maxLength={maxLength} />}
      </div>
      {error && (
        <Text variant="sm" className={style.error}>
          {error}
        </Text>
      )}
      {!error && helperText && (
        <Text variant="sm" className={style.helperText}>
          {helperText}
        </Text>
      )}
    </>
  );
});
